import React from 'react';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router";
import {API, Auth, graphqlOperation} from "aws-amplify";
import {listDashboardMaps} from "../graphql/queries";
import {
    CreateJourneyMapMutation,
    JourneyMap, ListDashboardMapsQuery,
} from "../API";
import '../i18n/config';
import {useUserInfo} from "../hooks/useUserInfo";
import {createJourneyMap, deleteJourneyMap, revokeMapAccess} from "../graphql/mutations";
import * as CJMapOp from "../models/Operation_gen";
import {SerializableJourneyMap} from "../models/SerializableJourneyMap";
import "./HeaderFooter.css";
import "./Dashboard.css";
import {MapTemplateSelector} from "./Dashboard/MapTemplateSelector";
import {Footer} from "./Dashboard/Footer";
import {generating_png} from "./Dashboard/generating_png";
import {debug_log, is_dev} from "../utils/debug";
import {network_error} from "../utils/browser";
import {CJMapManager} from "../models/CJmapManager";
import {fetchJourneyMapOperations} from "../graphql/MapOperation";
import {Operation} from "../models/Operation";
import {ManagerHistory} from "../models/ManagerHistory";
import {getMapMeta} from "../graphql/JourneyMap";
import {fetchJourneyMapOperationsEx, storeJourneyMapOperationsEx} from "../graphql/JourneyMapOperation";
import {CollaboratorDialog} from "./Collaboration/CollaboratorDialog";
import {MAP_VERSION1, MAP_VERSION2} from "../models/MapMeta";
import {ConfirmDialog} from "./common/ConfirmDialog";
import {MessageDialog, MessageDialogHandles} from "./common/MessageDialog";
import {DashboardMaps} from "../models/DashboardMaps";
import {NonOwnerCollaboratorDialog} from "./Collaboration/NonOwnerCollaboratorDialog";

async function createFromTemplate(user_name: string, title: string, json_string: string): Promise<JourneyMap> {
    const create_map_result = await API.graphql(graphqlOperation(
        createJourneyMap,
        {
            input: {
                owner: user_name,
                title: title,
                preview: generating_png,
                version: MAP_VERSION2,
            }
        }
    )) as { data: CreateJourneyMapMutation };

    const new_map = create_map_result.data.createJourneyMap;
    if (!new_map || !new_map.id) {
        throw new Error("新規カスタマージャーニーマップの作成に失敗しました");
    }

    const op = new CJMapOp.OpUpdateJourneyMap({journey_map: SerializableJourneyMap.fromJSONString(json_string)});
    const initial_operation_result = await storeJourneyMapOperationsEx(new_map.id, op);

    debug_log("createJourneyMap", create_map_result, initial_operation_result);

    return new_map;
}

async function fetchAllDashboardMaps() {
    const res = await API.graphql(graphqlOperation(listDashboardMaps)) as {data: ListDashboardMapsQuery};

    if (!res.data || !res.data.listDashboardMaps) {
        debug_log(res);
        throw new Error('listDashboardMaps failed');
    }

    return res.data.listDashboardMaps.filter((m): m is NonNullable<typeof m> => m !== null);
}

export const Dashboard = () => {
    const navigate = useNavigate();
    const {userName, userEmail} = useUserInfo();
    const [maps, setMaps] = React.useState(new DashboardMaps([]));
    const [map_loaded, setMapLoaded] = React.useState(false);
    const [showTemplate, setShowTemplate] = React.useState(false);
    const [is_waiting, setIsWaiting] = React.useState(false);
    const [collaboration_dialog_map_id, setCollaborationDialogMapId] = React.useState<string | null>(null);
    const [non_owner_collaboration_dialog_map_id, setNonOwnerCollaborationDialogMapId] = React.useState<string | null>(null);
    const [revoke_map, setRevokeMap] = React.useState<{id:string,name:string} | null>(null);

    const [deleting_map_id, setDeletingMapId] = React.useState<string | null>(null);

    const message_dialog = React.useRef<MessageDialogHandles>(null);

    const {t, i18n} = useTranslation();

    React.useEffect(() => {
        if (!userName) {
            return;
        }

        fetchAllDashboardMaps().catch(e => {
            debug_log(e);
            network_error();
        }).then((maps) => {
            if (maps) {
                setMaps(DashboardMaps.fromApi(maps));
                setMapLoaded(true);
            }
        });
    }, [userName]);

    const editMap = (map_id: string) => {
        navigate(`/m/${map_id}`);
    }

    const duplicateMap = async (map_id: string, original_title: string) => {
        setIsWaiting(true);

        const _duplicateMap = async () => {
            // @see loadJourneyMap (JourneyMapEditor.tsx)
            // ただし、マップが壊れているケースは考慮してない
            const mgr_history = new ManagerHistory();
            const map_meta = await getMapMeta(map_id);
            const raw_map_operations = (map_meta.Version === MAP_VERSION1) ?
                await fetchJourneyMapOperations(map_id)
                : await fetchJourneyMapOperationsEx(map_id);

            let last_mgr: CJMapManager = mgr_history.LastManager;
            for (const raw_op of raw_map_operations) {
                const op = Operation.fromStoredData(raw_op);
                [, last_mgr] = mgr_history.run(last_mgr, op);
            }

            const new_map_title = t("のコピー", {value: original_title});
            const new_map = await createMap(new_map_title, mgr_history.LastManager.toJourneyMapJSONString());
            await storeJourneyMapOperationsEx(new_map.id, new CJMapOp.OpUpdateTitle({title: new_map_title}));

            setMaps(maps.addApiJourneryMap(new_map));
        };

        try {
            await _duplicateMap();
        } catch (e) {
            debug_log(e instanceof Error ? e.message : e);
            await message_dialog.current?.showMessage("複製に失敗しました");
        }

        setIsWaiting(false);
    }

    const createMap = async (title: string, json_string: string) => {
        if (!maps.isCreatable) {
            message_dialog.current?.showMessage("カスタマージャーニーマップの作成上限です");
            throw new Error("over limit");
        }

        if (userName === undefined) {
            throw new Error("想定外のエラー");
        }

        return createFromTemplate(userName, title, json_string);
    }

    const showCollaborationDialog = (map_id: string) => {
        setCollaborationDialogMapId(map_id);
    }
    const closeCollaborationDialog = () => {
        setCollaborationDialogMapId(null);
    }
    const showNonOwnerCollaborationDialog = (map_id: string, map_name:string) => {
        setNonOwnerCollaborationDialogMapId(map_id);
        setRevokeMap({id: map_id, name: map_name});
    }
    const closeNonOwnerCollaborationDialog = (revoke: boolean) => {
        setNonOwnerCollaborationDialogMapId(null);
        !revoke && setRevokeMap(null);
    }

    return (
        <div onClick={() => setShowTemplate(false)}>
            {is_waiting && <div className={"waiting-cover"}/>}

            <ConfirmDialog title={t("削除確認")} ok={t("削除")} cancel={t("キャンセル")} okIsGray={true}
                           show={deleting_map_id !== null}
                           onOk={async () => {
                               if (deleting_map_id === null) {
                                   return;
                               }

                               await API.graphql(graphqlOperation(
                                   deleteJourneyMap,
                                   {input: {id: deleting_map_id}}
                               ));

                               setMaps(maps.removeMap(deleting_map_id));
                               setDeletingMapId(null);
                           }}
                           onCancel={() => {
                               setDeletingMapId(null);
                           }}
            >{t("復元できません。本当に削除しますか？")}</ConfirmDialog>

            <div className="header">
                {is_dev() &&
                  <div className="profile">
                    <span onClick={() => i18n.changeLanguage('en')}>[en]</span>
                    &nbsp;/&nbsp;
                    <span onClick={() => i18n.changeLanguage('ja')}>[ja]</span>
                  </div>
                }
                <div className="profile">{userEmail}</div>
                <span className="logout" role="button" onClick={() => Auth.signOut()}>{t("ログアウト")}</span>
            </div>
            <div className="dashboard">
                {collaboration_dialog_map_id !== null
                    && <CollaboratorDialog
                    mapId={collaboration_dialog_map_id}
                    show={true}
                    onClose={closeCollaborationDialog}
                  />}
                {non_owner_collaboration_dialog_map_id !== null
                    && <NonOwnerCollaboratorDialog
                    mapId={non_owner_collaboration_dialog_map_id}
                    show={true}
                    onClose={closeNonOwnerCollaborationDialog}
                  />}
                <ConfirmDialog title={t("アクセス権を削除")} ok={t("削除")} cancel={t("キャンセル")} okIsGray={true}
                               show={non_owner_collaboration_dialog_map_id === null && revoke_map !== null}
                               onOk={async () => {
                                   if (revoke_map === null) {
                                       return;
                                   }

                                   await API.graphql(graphqlOperation(
                                       revokeMapAccess,
                                       {mapId: revoke_map.id}
                                   ));

                                   setMaps(maps.removeMap(revoke_map.id));
                                   setRevokeMap(null);
                               }}
                               onCancel={() => {
                                   setRevokeMap(null);
                               }}
                >{t("「マップ名」へのアクセス権を本当に削除しますか？", {value: revoke_map && revoke_map.name})}</ConfirmDialog>


                <button type="button" className="new_button" onClick={(e) => {
                    setShowTemplate(!showTemplate);
                    e.stopPropagation();
                }}>{t("新規作成")}
                </button>
                {showTemplate && <MapTemplateSelector createMap={createMap}/>}

                {map_loaded && maps.hasPrivateMap &&
                  <div>
                    <h4 className="work-list-legend">{t("プライベート")}</h4>
                    <div className="works-list">
                        {maps.mappingPrivateMap(m =>
                            <div className="works" key={m.mapId} title={is_dev() ? `MapVersion:${m.version}` : ''}>
                                {m.collaboratorType === 'owner' && <span className="owner_mark" />}
                                <img src={"" + m.preview} alt="preview" className="preview"
                                     onClick={() => editMap(m.mapId)}
                                />
                                <span className="title">{m.title}</span>
                                <span className="works-menu-button"/>
                                <ul className="works-menu">
                                    <li onClick={() => editMap(m.mapId)}>{t("編集")}</li>
                                    {m.version === MAP_VERSION2 &&
                                      <li onClick={() => showCollaborationDialog(m.mapId)}>{t("共有")}</li>
                                    }
                                    <li onClick={() => duplicateMap(m.mapId, m.title)}>{t("複製")}</li>
                                    <li onClick={() => setDeletingMapId(m.mapId)}>{t("削除")}</li>
                                </ul>
                            </div>
                        )}
                    </div>
                  </div>
                }

                {map_loaded && maps.hasCollaboratedMap &&
                  <div>
                    <h4 className="work-list-legend">{t("共有中")}</h4>
                    <div className="works-list">
                        {maps.mappingCollaboratedMap(m =>
                            <div className="works" key={m.mapId} title={is_dev() ? `MapVersion:${m.version}` : ''}>
                                {m.collaboratorType === 'owner' && <span className="owner_mark" />}
                                <img src={"" + m.preview} alt="preview" className="preview"
                                     onClick={() => editMap(m.mapId)}
                                />
                                <span className="title">{m.title}</span>
                                <span className="works-menu-button"/>
                                <ul className="works-menu">
                                    <li onClick={() => editMap(m.mapId)}>{t("編集")}</li>
                                    {m.collaboratorType !== 'owner' && m.version === MAP_VERSION2 &&
                                        <li onClick={() => showNonOwnerCollaborationDialog(m.mapId, m.title)}>{t("共有")}</li>
                                    }
                                    {m.collaboratorType === 'owner' &&
                                      <>
                                        {m.version === MAP_VERSION2 && <li onClick={() => showCollaborationDialog(m.mapId)}>{t("共有")}</li>}
                                        <li onClick={() => duplicateMap(m.mapId, m.title)}>{t("複製")}</li>
                                        <li onClick={() => setDeletingMapId(m.mapId)}>{t("削除")}</li>
                                      </>
                                    }
                                </ul>
                            </div>
                        )}
                    </div>
                  </div>
                }

                {map_loaded && maps.empty &&
                  <div className="for-beginner">
                    <span>はじめての方へ：カスタマージャーニーマップの作り方</span>
                    <hr/>
                    <iframe width="560" height="315"
                            src="https://www.youtube.com/embed/-RTQIwq-BBM"
                            title="YouTube video player" frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen />
                  </div>
                }

            </div>
            <Footer/>
            <MessageDialog ref={message_dialog}/>
        </div>
    );
}
